import { useQuery } from '@tanstack/react-query';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { api, type CourseDetail } from '@/lib/api';
import { lockedLessonIds } from '@/lib/drip';
import { Card, CardContent } from '@/components/ui/card';
import { LessonRow } from '@/components/LessonRow';
import { ModuleBanner } from '@/components/ModuleBanner';

/**
 * One module: banner, description, its own progress, its own lessons. The
 * drill-down level between the course page and the lesson page.
 *
 * There is no module endpoint — `/api/courses/:slug` already returns the whole
 * tree, so this reuses the course page's query key verbatim. Arriving from the
 * course page is a cache hit: no second fetch.
 */
export default function Module() {
  const { slug = '', moduleId = '' } = useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ['course', slug],
    queryFn: () => api.get<CourseDetail>(`/api/courses/${slug}`),
    enabled: slug.length > 0,
    retry: (count, err) => {
      const msg = (err as Error).message;
      if (msg === 'course_locked' || msg === 'course_not_found') return false;
      return count < 2;
    },
  });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl p-6 text-sm text-muted-foreground">Lädt …</div>
    );
  }
  if (error) {
    const msg = (error as Error).message;
    // No cookie / session → not logged in. Send them to the login screen
    // (which offers log-in + the free-course signup) instead of a dead error.
    if (msg === 'not_authenticated') {
      return <Navigate to="/login" replace />;
    }
    if (msg === 'course_locked' || msg === 'course_not_found') {
      return <Navigate to="/" replace />;
    }
    return (
      <div className="mx-auto max-w-3xl p-6 text-sm text-destructive">
        Fehler beim Laden.
      </div>
    );
  }
  if (!data) return null;

  const { course, modules } = data;
  const module = modules.find((m) => m.id === moduleId);
  // A stale bookmark or a hand-typed id, or a module belonging to some other
  // course. The course itself loaded fine, so the nearest real surface is the
  // course page — never an empty module shell.
  if (!module) return <Navigate to={`/kurse/${slug}`} replace />;

  const completedCount = module.lessons.filter((l) => l.progress.completed).length;
  const pct =
    module.lessons.length === 0
      ? 0
      : Math.round((completedCount / module.lessons.length) * 100);

  // Walk the WHOLE course, not just this module: a lesson here is locked by any
  // incomplete lesson before it, including ones in earlier modules we don't render.
  const lockedIds = lockedLessonIds(modules, course.sequentialUnlock);

  // pb-40 clears the floating bottom nav. It has to be repeated on `sm:`
  // because `sm:p-6` is a shorthand and would otherwise reset the bottom
  // padding back to 1.5rem — the nav then sits on top of the last lesson.
  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4 pb-40 sm:p-6 sm:pb-40 sm:space-y-8">
      <div className="flex items-center justify-between">
        <Link
          to={`/kurse/${slug}`}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" /> Zurück zum Kurs
        </Link>
      </div>

      <section className="space-y-3">
        {/* The banner title is this page's h1 — on the course page it's one of
            many, so there it stays the h2 it has always been. */}
        <ModuleBanner as="h1" title={module.title} imageUrl={module.imageUrl} />
        {module.description && (
          <p className="text-sm text-muted-foreground">{module.description}</p>
        )}
        {/* No lessons → no bar. "0/0 Lektionen · 0%" reads as a stalled module
            rather than an empty one; the Home course cards guard the same way. */}
        {module.lessons.length > 0 && (
          <div className="space-y-1.5 pt-1">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {completedCount}/{module.lessons.length} Lektionen
              </span>
              <span>{pct}%</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}
      </section>

      {module.lessons.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            Hier gibt es noch nichts zu sehen. Schau später wieder vorbei.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {module.lessons.map((l, idx) => (
            <LessonRow
              key={l.id}
              lesson={l}
              number={idx + 1}
              locked={lockedIds.has(l.id)}
              to={`/kurse/${slug}/${module.id}/${l.id}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
